import React, { useMemo } from 'react';
import { AlertTriangle, TrendingUp, AlertCircle } from 'lucide-react';

interface AgeingBarChartProps {
  data: {
    current: number;
    days1_30: number;
    days31_60: number;
    days61_90: number;
    days90Plus: number;
  };
  className?: string;
  onSendReminders?: () => void;
  onEscalateToCollections?: () => void;
}

interface AgeingCategory {
  label: string;
  value: number;
  percentage: number;
  color: string;
  bgColor: string;
  gradientFrom: string;
  gradientTo: string;
  textColor: string;
  isOverdue: boolean;
  daysRange: string;
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
}

export const AgeingBarChart: React.FC<AgeingBarChartProps> = ({ data, className = '', onSendReminders, onEscalateToCollections }) => {
  const { categories, total, overdueTotal, overduePercentage, currentPercentage } = useMemo(() => {
    const total = data.current + data.days1_30 + data.days31_60 + data.days61_90 + data.days90Plus;
    const overdueTotal = data.days1_30 + data.days31_60 + data.days61_90 + data.days90Plus;
    const overduePercentage = total > 0 ? (overdueTotal / total) * 100 : 0;
    const currentPercentage = total > 0 ? (data.current / total) * 100 : 0;

    const categories: AgeingCategory[] = [
      {
        label: 'Current',
        value: data.current,
        percentage: total > 0 ? (data.current / total) * 100 : 0,
        color: '#14b8a6',
        bgColor: 'bg-gradient-to-r from-teal-400 to-teal-500',
        gradientFrom: 'from-teal-400',
        gradientTo: 'to-teal-500',
        textColor: 'text-teal-700',
        isOverdue: false,
        daysRange: '0-30 days',
        riskLevel: 'low',
      },
      {
        label: '31-60 Days',
        value: data.days1_30,
        percentage: total > 0 ? (data.days1_30 / total) * 100 : 0,
        color: '#14b8a6',
        bgColor: 'bg-gradient-to-r from-teal-400 to-teal-500',
        gradientFrom: 'from-teal-400',
        gradientTo: 'to-teal-500',
        textColor: 'text-teal-700',
        isOverdue: true,
        daysRange: '31-60 days',
        riskLevel: 'medium',
      },
      {
        label: '61-90 Days',
        value: data.days31_60,
        percentage: total > 0 ? (data.days31_60 / total) * 100 : 0,
        color: '#14b8a6',
        bgColor: 'bg-gradient-to-r from-teal-400 to-teal-500',
        gradientFrom: 'from-teal-400',
        gradientTo: 'to-teal-500',
        textColor: 'text-teal-700',
        isOverdue: true,
        daysRange: '61-90 days',
        riskLevel: 'high',
      },
      {
        label: '91-120 Days',
        value: data.days61_90,
        percentage: total > 0 ? (data.days61_90 / total) * 100 : 0,
        color: '#14b8a6',
        bgColor: 'bg-gradient-to-r from-teal-400 to-teal-500',
        gradientFrom: 'from-teal-400',
        gradientTo: 'to-teal-500',
        textColor: 'text-teal-700',
        isOverdue: true,
        daysRange: '91-120 days',
        riskLevel: 'critical',
      },
      {
        label: 'Over 120 Days',
        value: data.days90Plus,
        percentage: total > 0 ? (data.days90Plus / total) * 100 : 0,
        color: '#14b8a6',
        bgColor: 'bg-gradient-to-r from-teal-400 to-teal-500',
        gradientFrom: 'from-teal-400',
        gradientTo: 'to-teal-500',
        textColor: 'text-teal-700',
        isOverdue: true,
        daysRange: '120+ days',
        riskLevel: 'critical',
      },
    ];

    return { categories, total, overdueTotal, overduePercentage, currentPercentage };
  }, [data]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const getRiskBadge = (riskLevel: string, value: number) => {
    if (value === 0) return null;
    
    switch (riskLevel) {
      case 'critical':
        return (
          <div className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
            <AlertTriangle className="h-3 w-3" />
            Critical
          </div>
        );
      case 'high':
        return (
          <div className="flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
            <AlertCircle className="h-3 w-3" />
            High Risk
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Header Summary - Compact */}
      <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg border border-slate-200">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 bg-white rounded-lg shadow-sm">
            <TrendingUp className="h-4 w-4 text-slate-600" />
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Total Outstanding</div>
            <div className="text-xl font-bold text-foreground leading-tight">{formatCurrency(total)}</div>
          </div>
        </div>
        
        <div className="flex gap-5">
          <div className="text-right">
            <div className="text-xs text-muted-foreground">Current</div>
            <div className="text-base font-semibold text-teal-600 leading-tight">{formatCurrency(data.current)}</div>
            <div className="text-xs text-muted-foreground">{currentPercentage.toFixed(1)}%</div>
          </div>
          {overdueTotal > 0 && (
            <div className="text-right">
              <div className="text-xs text-muted-foreground">Overdue</div>
              <div className="text-base font-semibold text-teal-600 leading-tight">{formatCurrency(overdueTotal)}</div>
              <div className="text-xs text-muted-foreground">{overduePercentage.toFixed(1)}%</div>
            </div>
          )}
        </div>
      </div>

      {/* Enhanced Horizontal Bars - Compact */}
      <div className="space-y-1.5">
        {categories.map((category, index) => (
          <div
            key={index}
            className="group relative"
          >
            {/* Row Container - Reduced padding */}
            <div className="flex items-center gap-3 px-3 py-2 rounded-lg border border-border bg-card hover:shadow-md transition-all duration-300 hover:border-slate-300">
              {/* Left: Label & Badge */}
              <div className="min-w-[170px] flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                    {category.label}
                  </div>
                  <div className="text-xs text-muted-foreground">{category.daysRange}</div>
                </div>
              </div>

              {/* Center: Progress Bar - Reduced height */}
              <div className="flex-1 relative">
                <div className="h-7 bg-slate-100 rounded-md overflow-hidden border border-slate-200 shadow-inner">
                  {category.value > 0 && (
                    <div
                      className={`h-full ${category.bgColor} transition-all duration-700 ease-out flex items-center justify-between px-3 shadow-sm relative group-hover:shadow-md`}
                      style={{ 
                        width: `${Math.max(category.percentage, 0)}%`,
                        minWidth: '50px'
                      }}
                    >
                      {/* Shine effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      
                      {/* Percentage inside bar */}
                      {category.percentage > 8 && (
                        <span className="text-xs font-bold text-white drop-shadow-md z-10 relative">
                          {category.percentage.toFixed(1)}%
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Right: Amount & Percentage */}
              <div className="min-w-[130px] text-right">
                <div className="text-base font-bold text-foreground leading-tight">
                  {formatCurrency(category.value)}
                </div>
                {category.percentage <= 8 && category.value > 0 && (
                  <div className={`text-xs font-medium ${category.textColor}`}>
                    {category.percentage.toFixed(1)}%
                  </div>
                )}
                {category.value === 0 && (
                  <div className="text-xs text-muted-foreground">
                    0.0%
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
